import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Receipt, Users, CreditCard, Loader2, ShieldCheck, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const tsToDate = (ts) => (ts ? new Date(Number(ts) * 1000).toLocaleDateString() : '');

// Only rendered when the practice collects the patient fee itself and we invoice
// a fixed share per document at month end (ADR 0001, Mode 3). Deliberately shows
// count × unit price so a practice manager can reconcile the invoice line by hand.
// ADR 0004. A Plan C practice has no per-document fee at all — we take nothing
// from its patients and price its ORGANISATION's month. Without this card the
// billing page would simply show no submissions, which reads as "these are
// free"; with it, it says what is true and claims no invoice line, because Plan
// C's invoice does not exist yet (ADR 0003 blocker 3).
function PlanCSubmissionsCard({ fees, planC }) {
  if (!planC) return null;
  return (
    <Card data-testid="admin-planc-submissions">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" /> Submissions — {fees?.period_label}
        </CardTitle>
        <CardDescription className="text-xs">
          You set what your patients pay, if anything, and you keep all of it — we never take a percentage
          and never charge your patients. Your submissions are priced across your organisation, not per patient.
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">Submissions sent to a provider this month</span>
          <span className="tabular-nums font-semibold" data-testid="admin-planc-submissions-count">{fees?.documents ?? 0}</span>
        </div>
        <p className="text-xs text-muted-foreground pt-2">
          A submission counts once it reaches a provider for a decision — patient drafts, and cases still
          with your staff, are never counted.
        </p>
      </CardContent>
    </Card>
  );
}

function DocumentFeesCard({ fees, autopay }) {
  if (!fees?.applies) return null;
  // GAPS F13: without a subscription that still drafts invoices there is no
  // invoice to add a line to, so month-end fees arrive as an emailed pay-by-link
  // invoice instead. Mirrors subscriptions.can_bill_on_subscription.
  const onAutopay = autopay?.configured
    && !['canceled', 'cancelled', 'incomplete', 'incomplete_expired', 'paused'].includes(autopay.status);
  return (
    <Card data-testid="admin-document-fees">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" /> Document fees — {fees.period_label}
        </CardTitle>
        <CardDescription className="text-xs">
          You bill your patients directly. We charge {fmt(fees.unit_price)} per submitted document
          {onAutopay
            ? ', added to your next invoice on top of your plan and seats.'
            : ' — at month end we email you one invoice for the month, payable by link.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm space-y-1.5">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">
            Documents submitted this month × {fmt(fees.unit_price)}
          </span>
          <span className="tabular-nums" data-testid="admin-document-fees-count">{fees.documents}</span>
        </div>
        <div className="flex justify-between font-semibold pt-2 border-t mt-2">
          <span>{onAutopay ? 'Accruing for your next invoice' : 'Accruing for your month-end invoice'}</span>
          <span className="tabular-nums" data-testid="admin-document-fees-total">{fmt(fees.accrued_total)}</span>
        </div>
        {fees.invoiced_total > 0 && (
          <div className="flex justify-between text-xs text-muted-foreground pt-1">
            <span>Already invoiced this month</span>
            <span className="tabular-nums">{fmt(fees.invoiced_total)}</span>
          </div>
        )}
        <p className="text-xs text-muted-foreground pt-1">
          A document counts once it reaches your review queue — patient drafts that were never
          submitted are never billed.
        </p>
      </CardContent>
    </Card>
  );
}

function AutoPayCard({ autopay, busy, onSetup, onCancel }) {
  if (!autopay) {
    return (
      <Card data-testid="admin-autopay-card">
        <CardContent className="py-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking auto-pay status…
        </CardContent>
      </Card>
    );
  }
  const active = autopay.configured && autopay.status === 'active';
  const pastDue = autopay.configured && autopay.status === 'past_due';
  // The owner has waived this practice's base plan + seats. Say so instead of
  // offering a setup button the API would refuse with a 400.
  if (autopay.saas_exempt) {
    return (
      <Card data-testid="admin-autopay-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" /> Auto-pay
            <Badge variant="secondary" data-testid="admin-autopay-status">Not billed</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p className="text-muted-foreground" data-testid="admin-autopay-exempt">
            {autopay.reason || 'Your practice is not billed for the monthly subscription.'}
          </p>
          <p className="text-xs text-muted-foreground">
            Per-document fees, where they apply, are invoiced separately.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="admin-autopay-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" /> Auto-pay
          {autopay.configured && (
            <Badge variant={active ? 'default' : pastDue ? 'destructive' : 'secondary'} data-testid="admin-autopay-status">
              {autopay.cancel_at_period_end ? 'Ends at period end' : active ? 'Active' : pastDue ? 'Past due' : autopay.status}
            </Badge>
          )}
          {autopay.mode === 'test' && <Badge variant="outline" className="text-[10px]">Test mode</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        {!autopay.configured ? (
          <>
            <p className="text-muted-foreground">
              Save a card once and your monthly subscription (base plan + any extra seats) charges automatically —
              seat changes are prorated to the day and appear on your next invoice, never as a separate charge.
              Powered by Stripe; card details never touch our servers.
            </p>
            <Button onClick={onSetup} disabled={busy === 'setup'} data-testid="admin-autopay-setup">
              {busy === 'setup' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
              Set up auto-pay with Stripe
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {autopay.card_last4 && (
                <span data-testid="admin-autopay-card-info">
                  <span className="text-muted-foreground">Card:</span>{' '}
                  <span className="capitalize font-medium">{autopay.card_brand}</span> •••• {autopay.card_last4}
                </span>
              )}
              {autopay.current_period_end && (
                <span>
                  <span className="text-muted-foreground">{autopay.cancel_at_period_end ? 'Access until:' : 'Next charge:'}</span>{' '}
                  <span className="font-medium">{tsToDate(autopay.current_period_end)}</span>
                </span>
              )}
            </div>
            {pastDue && (
              <p className="text-xs flex items-start gap-1.5 text-[hsl(0_72%_40%)]" data-testid="admin-autopay-pastdue">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Your last charge failed. Stripe will retry automatically — or contact support to update the card.
              </p>
            )}
            {!autopay.cancel_at_period_end && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={busy === 'cancel'} data-testid="admin-autopay-cancel">
                    {busy === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Cancel auto-pay
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel auto-pay?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Your subscription stays active until the end of the current period, then stops renewing.
                      You can set it up again anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-autopay-cancel-abort">Keep auto-pay</AlertDialogCancel>
                    <AlertDialogAction onClick={onCancel} data-testid="admin-autopay-cancel-confirm">Cancel auto-pay</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminBilling() {
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [autopay, setAutopay] = useState(null);
  const [busy, setBusy] = useState('');
  const [params, setParams] = useSearchParams();

  const loadAutopay = useCallback(async (sessionId) => {
    try {
      const r = await api.get('/billing/subscription/status', { params: sessionId ? { session_id: sessionId } : {} });
      setAutopay(r.data);
      return r.data;
    } catch (e) {
      setAutopay({ configured: false, mode: 'unavailable' });
      return null;
    }
  }, []);

  useEffect(() => {
    // A 403 is the `view_billing` toggle, not an outage — say which, rather
    // than leaving "Loading…" on screen for ever.
    api.get('/admin/billing').then(r => setD(r.data)).catch((e) => setLoadError(
      e?.response?.status === 403
        ? "You don't have billing access. Ask a practice admin to turn on \"View billing and invoices\" for you."
        : errText(e, 'Could not load billing')));
    const sessionId = params.get('session_id');
    if (sessionId) {
      // Returned from Stripe Checkout — finalize + confirm
      loadAutopay(sessionId).then((s) => {
        if (s?.configured && s.status === 'active') toast.success('Auto-pay is live — your card will be charged monthly');
        else if (s?.configured) toast.message('Auto-pay set up — first charge is processing');
      });
      params.delete('session_id');
      setParams(params, { replace: true });
    } else {
      if (params.get('canceled')) {
        toast.message('Checkout canceled — no card was saved');
        params.delete('canceled');
        setParams(params, { replace: true });
      }
      loadAutopay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setup = async () => {
    setBusy('setup');
    try {
      const r = await api.post('/billing/subscription/checkout', { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errText(e, 'Could not start Stripe checkout'));
      setBusy('');
    }
  };

  const cancel = async () => {
    setBusy('cancel');
    try {
      const r = await api.post('/billing/subscription/cancel');
      setAutopay(r.data);
      toast.success('Auto-pay will end at the close of the current period');
    } catch (e) {
      toast.error(errText(e, 'Could not cancel'));
    } finally {
      setBusy('');
    }
  };

  if (loadError) return <div className="p-6 text-sm text-muted-foreground" data-testid="admin-billing-error">{loadError}</div>;
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const inv = d.invoice;
  const realInvoices = autopay?.configured && autopay.invoices?.length ? autopay.invoices : null;
  // GAPS F7: without a Stripe subscription there is no invoice history — only
  // demo practices get (flagged) sample rows.
  const rows = realInvoices || d.invoices || [];
  const samples = !realInvoices && d.invoices_are_samples;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-4">
      <h1 className="text-base lg:text-lg font-semibold tracking-tight">Billing</h1>

      <Card data-testid="admin-billing-invoice">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your subscription — {inv?.period}</CardTitle>
          <CardDescription className="text-xs">
            Computed live from your active seats. Status: <span className="capitalize font-medium">{d.practice?.subscription_status}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-1.5">
          {inv?.lines?.map((l) => (
            <div key={l.key} className="flex justify-between gap-3">
              <span className="text-muted-foreground">{l.label}{l.qty > 1 ? ` ×${l.qty}` : ''}</span>
              <span className="tabular-nums">{fmt(l.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between font-semibold pt-2 border-t mt-2">
            <span>Total / month</span>
            <span className="tabular-nums" data-testid="admin-billing-total">{fmt(inv?.total_monthly)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Badge variant="outline" className="text-[10px]">
              <Users className="h-3 w-3 mr-1" />
              {inv?.seat_usage?.doctor} doctor · {inv?.seat_usage?.admin} admin · {inv?.seat_usage?.staff} staff
            </Badge>
            <Link to="/admin/users" className="text-xs text-primary hover:underline" data-testid="admin-billing-manage-seats">Manage seats →</Link>
          </div>
          <p className="text-xs text-muted-foreground pt-1">
            Plan includes 1 doctor, 1 admin and 3 staff seats. Extra doctor $75/mo · extra admin $15/mo · extra staff $5/mo, added automatically.
            A seat added or removed mid-month is charged (or credited) for the days you had it, so your next invoice can
            differ from the total above by a partial-month amount.
          </p>
        </CardContent>
      </Card>

      <DocumentFeesCard fees={d.document_fees} autopay={autopay} />

      <PlanCSubmissionsCard fees={d.document_fees} planC={d.pricing_model === 'planc'} />

      <AutoPayCard autopay={autopay} busy={busy} onSetup={setup} onCancel={cancel} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" /> Invoice history
            {realInvoices && <Badge variant="outline" className="text-[10px]">From Stripe</Badge>}
            {samples && <Badge variant="secondary" className="text-[10px]" data-testid="admin-billing-samples">Sample data — not real charges</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Phone: card list */}
          <div className="md:hidden space-y-2" data-testid="admin-billing-cards">
            {rows.length === 0 && (
              <p className="py-6 text-center text-xs text-muted-foreground">No invoices yet. Once auto-pay is set up, your monthly Stripe invoices appear here.</p>
            )}
            {rows.map(inv2 => (
              <div key={inv2.id} className="rounded-lg border p-3 flex items-start justify-between gap-3 text-sm" data-testid={`admin-invoice-card-${inv2.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {realInvoices && inv2.hosted_url
                      ? <a href={inv2.hosted_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{inv2.id}</a>
                      : (inv2.period || inv2.id)}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {inv2.status}{' · '}{realInvoices ? tsToDate(inv2.paid_at) : ((inv2.paid_at || '').slice(0, 10) || '—')}
                  </p>
                </div>
                <span className="tabular-nums font-medium shrink-0">{fmt(inv2.amount)}</span>
              </div>
            ))}
          </div>

          {/* Tablet and up: table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-billing-table">
            <thead className="bg-card border-b"><tr className="text-left text-xs text-muted-foreground"><th className="px-3 py-2">{realInvoices ? 'Invoice' : 'Period'}</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Paid at</th></tr></thead>
            <tbody>
              {rows.length === 0 && (
                <tr data-testid="admin-billing-no-invoices">
                  <td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No invoices yet. Once auto-pay is set up, your monthly Stripe invoices appear here.
                  </td>
                </tr>
              )}
              {rows.map(inv2 => (
                <tr key={inv2.id} className="border-b" data-testid={`admin-invoice-row-${inv2.id}`}>
                  <td className="px-3 py-2">
                    {realInvoices && inv2.hosted_url
                      ? <a href={inv2.hosted_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{inv2.id}</a>
                      : (inv2.period || inv2.id)}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{fmt(inv2.amount)}</td>
                  <td className="px-3 py-2 capitalize">{inv2.status}</td>
                  <td className="px-3 py-2">{realInvoices ? tsToDate(inv2.paid_at) : ((inv2.paid_at || '').slice(0, 10) || '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
